import React, { useEffect, useCallback, useContext, useMemo, MouseEventHandler, useState } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchNotificationsForSlug, dismissNotificationBanner } from 'actions/ContentActions'
import { SITEWIDE_NOTIFICATION_SLUG } from 'constants/content'
import Image from 'components/Common/Image'
import GeoContext from 'contexts/geoContext'
import PageBanner from 'LuxKit/Banners/PageBanner'
import { getNotificationBannerCookieName } from 'cookies/notificationBannerCookie'
import { encode, normalizeBase64 } from 'lib/encoding/isomorphicBase64'
import config from 'constants/config'
import { getClosestAirport } from 'selectors/flightsSelectors'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import CookiesContext from 'contexts/CookiesContext'
import TextLink from 'LuxKit/TextLink'

interface Props {
  notificationBanner: App.NotificationBanner | null
  slug?: string
  postcode?: string
}

const VICStoreBanner: App.NotificationBanner = {
  bannerText: 'Book your next escape in-store at Luxury Escapes, Chadstone Shopping Centre',
  linkText: 'BOOK NOW',
  linkTo: 'https://luxuryescapes.com/au/campaign/store',
  openInNewTab: false,
  id: 'defaultNotificationBanner',
  slug: 'defaultNotificationBanner',
  show: true,
}

const NSWStoreBanner: App.NotificationBanner = {
  bannerText: "You're Invited: Westfield Bondi Grand Opening Wed 12 Nov, from 9:30am. $100k in giveaways to be won",
  linkText: 'Learn more',
  linkTo: 'https://luxuryescapes.com/au/campaign/store',
  openInNewTab: false,
  id: 'defaultNotificationBanner',
  slug: 'defaultNotificationBanner',
  show: true,
}

function NotificationBanner(props: Props) {
  const {
    slug = SITEWIDE_NOTIFICATION_SLUG,
    notificationBanner,
    postcode,
  } = props
  const dispatch = useAppDispatch()

  const { getCookie } = useContext(CookiesContext)
  const geo = useContext(GeoContext)
  const closestAirport = useAppSelector(getClosestAirport)
  const isDesktop = useScreenSizeUp('desktop')
  const [isDismissed, setIsDismissed] = useState(false)

  const banner = useMemo(() => {
    if (slug === SITEWIDE_NOTIFICATION_SLUG || slug === 'none') {
      if (isEnabledForFeature(config.CHADSTONE_STORE_BANNER_REGIONS, geo.currentRegionCode)) {
        const notificationBannerCookie = getCookie(getNotificationBannerCookieName(slug)) || ''

        // Victorian and New South Welshman users get dedicated store banners. We still show
        // these even when no site-wide banner exists, but respect dismissals via the cookie.
        const isVic = geo.geoStateName === 'VIC'
        const isVicPostcode = postcode?.startsWith('3')
        const isVicAirport = closestAirport?.code === 'MEL'

        if (isVic || isVicPostcode || isVicAirport) {
          const encodedBannerText = encode(VICStoreBanner.bannerText)
          return {
            ...VICStoreBanner,
            show: isDesktop && encodedBannerText !== null && normalizeBase64(encodedBannerText) !== normalizeBase64(notificationBannerCookie),
          }
        }

        const isNsw = geo.geoStateName === 'NSW'
        const isNswPostcode = postcode?.startsWith('2')
        const isNswAirport = closestAirport?.code === 'SYD'

        if (isNsw || isNswPostcode || isNswAirport) {
          const encodedBannerText = encode(NSWStoreBanner.bannerText)
          return {
            ...NSWStoreBanner,
            show: isDesktop && encodedBannerText !== null && normalizeBase64(encodedBannerText) !== normalizeBase64(notificationBannerCookie),
          }
        }
      }
    }

    return notificationBanner
  }, [slug, geo.currentRegionCode, geo.geoStateName, notificationBanner, postcode, closestAirport, getCookie, isDesktop])

  useEffect(() => {
    if (slug) {
      dispatch(fetchNotificationsForSlug(slug, getCookie))
    }
  }, [dispatch, getCookie, slug])

  const onDismiss = useCallback(() => {
    if (banner) {
      dispatch(dismissNotificationBanner(slug, banner.bannerText))
      setIsDismissed(true)
    }
  }, [dispatch, slug, banner])

  const handleLinkClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (banner?.openInPopup) {
      e.preventDefault()

      window.open(banner.linkTo, 'popup', 'width=640,height=640')
    }
  }, [banner])

  if (isDismissed || !banner?.show) {
    return null
  }

  const { openInNewTab, openInPopup, bannerText, linkText, linkTo, image } = banner
  const isExternalLink = linkTo?.startsWith('https://')

  return <PageBanner
    id="notification-page-banner"
    className="no-print"
    data-testid="notification-page-banner"
    kind="promotional-primary"
    icon={image ? <div><Image id={image.id} width={image.width} alt={image.alt} enhancedFormats={false} quality="best" /></div> : undefined}
    onDismiss={onDismiss}
    tabletAlign="center"
  >
    {bannerText}
    {(linkTo && linkText) && <>
      {' '}
      <TextLink
        rel="noopener"
        size="medium"
        target={openInNewTab ? '_blank' : (openInPopup ? 'popup' : undefined)}
        onClick={handleLinkClick}
        // To prevent auto-append current region to external links
        to={!isExternalLink ? linkTo : undefined}
        href={isExternalLink ? linkTo : undefined}
      >
        {linkText}
      </TextLink>
    </>}
  </PageBanner>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    notificationBanner: state.content.notifications[ownProps.slug || SITEWIDE_NOTIFICATION_SLUG],
    postcode: state.auth.account?.postcode,
  }
}

export default connect(mapStateToProps)(NotificationBanner)
